// Types
import { PaymentType } from 'types/Constants.types';
import { PaymentDetailsProps } from './PaymentDetails.types';

// Config
import { paymentTypeConfig, paymentTypeLabels } from './PaymentDetails.constants';
import paymentLogoConstants from '@constants/paymentLogoConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import supportedCreditCardTypes from '@constants/supportedCreditCardTypes';
import { getLast4Digits } from '@utils/helpers/cardDetails';

// Components
import CreditRemainingAmount from '@components/account-credit-remaining-amount';
import Container from '@components/container';
import Heading from '@components/heading';

// Styles
import styles from './payment-details.scss';

const PaymentDetails = ({ payments = [] }: PaymentDetailsProps) => {
  const supportedCardTypes = [
    supportedCreditCardTypes.AMERICAN_EXPRESS,
    supportedCreditCardTypes.MAESTRO,
    supportedCreditCardTypes.MASTERCARD,
    supportedCreditCardTypes.ANY_DAY,
    supportedCreditCardTypes.NEW_DAY,
    supportedCreditCardTypes.VISA,
    supportedCreditCardTypes.VISA_DEBIT,
  ];

  let index = 0;
  const paymentItems = payments.map((payment = {}) => {
    const paymentType: PaymentType = (payment.type ?? '').split(':')[0] as PaymentType;
    let imageProps;

    if (!paymentType) {
      return null;
    }
    index++;

    const src = paymentLogoConstants[paymentType] || '';

    switch (paymentType) {
      case paymentTypeConstants.CREDIT_CARD:
      case paymentTypeConstants.TOKENIZED_PAYMENT_CARD:
      case paymentTypeConstants.SAVED_PAYMENT_CARD: {
        const type = payment?.details?.creditCardType ?? '';

        imageProps = {
          src: paymentLogoConstants[type] || src,
          alt: type,
        };
        break;
      }
      default:
        if (src) {
          imageProps = {
            src,
            alt: paymentType,
          };
        }
        break;
    }

    const cardType =
      paymentType === paymentTypeConstants.CREDIT_CARD ||
      paymentType === paymentTypeConstants.TOKENIZED_PAYMENT_CARD ||
      paymentType === paymentTypeConstants.SAVED_PAYMENT_CARD;

    const cardWithRemainingBalance = payment?.cardWithRemainingBalance;
    const isPosCredit = paymentType?.includes(paymentTypeConstants.POS_CREDIT) || false;
    const amountLabel = isPosCredit ? 'Agreement no.' : 'Amount';
    const amountText = isPosCredit
      ? `ending in ${payment?.posCreditDetails?.agreementNumberLastDigits}`
      : payment?.amount;

    const cardTestId = paymentTypeConfig[paymentType] || '';
    let cardNumber = payment?.details?.maskedNumber ?? payment?.details?.lastDigits ?? '';

    if (cardNumber.length > 4) {
      cardNumber = getLast4Digits(cardNumber);
    }

    return (
      <Container key={paymentType} marginTop={index === 1 ? undefined : '4'}>
        <dl className="m-0" data-testid="payment-details-item">
          {(!cardType || (cardType && !!supportedCardTypes.includes(payment?.details?.creditCardType ?? ''))) && (
            <Container
              flex={{ alignItems: 'center', gap: '2', justifyContent: 'space-between' }}
              marginBottom="1"
              testId="payment-details-item-type"
            >
              <dt>Payment type:</dt>
              {imageProps ? (
                <dd>
                  <img
                    {...imageProps}
                    alt={imageProps.alt}
                    className={styles.paymentTypeLogo}
                    data-testid={`payment-details-image-${paymentType}`}
                  />
                </dd>
              ) : (
                <dd>
                  <Heading label={paymentTypeLabels[paymentType]} tag="span" type="xs" />
                </dd>
              )}
            </Container>
          )}

          {cardNumber && (
            <Container flex={{ gap: '2', justifyContent: 'space-between' }} marginBottom="1" testId={cardTestId}>
              <dt>Card number:</dt>
              <dd>{cardNumber}</dd>
            </Container>
          )}

          <Container flex={{ gap: '2', justifyContent: 'space-between' }} testId="payment-details-item-amount">
            <dt>{amountLabel}</dt>
            <dd>{amountText}</dd>
          </Container>

          {paymentType === paymentTypeConstants.GIFT_CARD && cardWithRemainingBalance && (
            <CreditRemainingAmount
              amount={cardWithRemainingBalance.remainingBalance}
              cardEnding={cardWithRemainingBalance.endNumber}
              className={styles.creditRemaining}
              testId="payment-details-gift-card-credit-remaining"
              type={paymentTypeConstants.GIFT_CARD}
            />
          )}
        </dl>
      </Container>
    );
  });

  return (
    <Container background="white" className={styles.container} paddingBottom="3" paddingX="3">
      {paymentItems}
    </Container>
  );
};

export default PaymentDetails;
